import { useEffect, useMemo } from 'react'
import { useQuickLinksStore } from '../stores/useQuickLinksStore.js'
import { useGetQuickLinks, useQuickLinksUpdate } from '../new/query.js'
import useUserStore from '../stores/user-store.jsx'
import { canAccessQuickLink } from '../auth/permission-paths.js'

function buildApplicationUrl(applicationId, segment) {
  return segment
    ? `/client/applications/${applicationId}/${segment}`
    : `/client/applications/${applicationId}`
}

function toActiveLinks(availableLinks, savedLinks, applicationId) {
  const availableById = new Map(availableLinks.map((link) => [link.id, link]))

  return [...savedLinks]
    .sort((left, right) => left.ordering - right.ordering)
    .map((savedLink) => {
      const availableLink = availableById.get(savedLink.linkId)

      if (!availableLink) {
        return null
      }

      return {
        id: availableLink.id,
        label: availableLink.name,
        url: buildApplicationUrl(applicationId, availableLink.url),
        segment: availableLink.url,
      }
    })
    .filter(Boolean)
}

export function useBootstrapQuickLinks() {
  const hydrated = useQuickLinksStore((state) => state.hydrated)
  const setQuickLinksData = useQuickLinksStore((state) => state.setQuickLinksData)
  const query = useGetQuickLinks(!hydrated)

  useEffect(() => {
    if (query.data && !hydrated) {
      setQuickLinksData(query.data)
    }
  }, [hydrated, query.data, setQuickLinksData])

  return query
}

export function useQuickLinksData(applicationId) {
  const availableLinks = useQuickLinksStore((state) => state.availableLinks)
  const savedLinks = useQuickLinksStore((state) => state.savedLinks)
  const hydrated = useQuickLinksStore((state) => state.hydrated)
  const permissions = useUserStore((state) => state.permissions)
  const query = useGetQuickLinks(!hydrated)
  const accessibleAvailableLinks = useMemo(
    () => availableLinks.filter((link) => canAccessQuickLink(permissions, link)),
    [availableLinks, permissions],
  )

  const activeLinks = useMemo(
    () => toActiveLinks(accessibleAvailableLinks, savedLinks, applicationId),
    [applicationId, accessibleAvailableLinks, savedLinks],
  )

  return {
    availableLinks: accessibleAvailableLinks,
    savedLinks,
    activeLinks,
    isLoading: !hydrated && query.isLoading,
    isError: !hydrated && query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

export function useSaveQuickLinksMutation(availableLinks) {
  const setSavedLinks = useQuickLinksStore((state) => state.setSavedLinks)
  const savedLinks = useQuickLinksStore((state) => state.savedLinks)
  const mutation = useQuickLinksUpdate((data) => {
    setSavedLinks(data.userLinks)
  })

  return {
    ...mutation,
    mutateAsync: async (draftActiveLinks) => {
      const availableById = new Map(availableLinks.map((link) => [link.id, link]))
      const userLinks = draftActiveLinks
        .map((link, index) => {
          const availableLink = availableById.get(link.id)

          if (!availableLink) {
            return null
          }

          return {
            linkId: availableLink.id,
            linkName: availableLink.name,
            linkUrl: availableLink.url,
            ordering: index + 1,
          }
        })
        .filter(Boolean)

      const previousUserLinks = savedLinks

      setSavedLinks(userLinks)

      try {
        return await mutation.mutateAsync({
          availableLinks,
          userLinks,
        })
      } catch (error) {
        setSavedLinks(previousUserLinks)
        throw error
      }
    },
  }
}
